import React, { useEffect, useState } from 'react';
import './ColaboradorDashboard.css';
import { FaHourglassHalf, FaCheck, FaInfoCircle, FaProjectDiagram, FaTimes, FaCheckCircle, FaCalendar, FaUsers, FaExclamationCircle, FaTasks, FaChevronRight, FaUser, FaComment, FaLink } from 'react-icons/fa';
import { fetchAuth } from '../../utils/api';
import { useLocation } from 'react-router-dom';

export default function ColaboradorInvitaciones() {
  const [projects, setProjects] = useState({ solicitudes: [], aceptados: [] });
  const [projectDetail, setProjectDetail] = useState(null);
  const [projectDetailOpen, setProjectDetailOpen] = useState(false);
  const [modalExpanded, setModalExpanded] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const location = useLocation();
  const isSolicitudesView = location.pathname.includes('/colaborador/invitaciones');
  const isProyectosView = location.pathname.includes('/colaborador/proyectos');

  useEffect(() => {
    fetchProjects();
  }, []);

  const fetchProjects = async () => {
    try {
      setLoading(true);
      setError(null);
      const data = await fetchAuth('/api/colaborador/mis-proyectos/');
      const aceptados = Array.isArray(data.aceptados) ? data.aceptados : [];
      const solicitudes = Array.isArray(data.solicitudes) ? data.solicitudes : [];
      setProjects({ solicitudes, aceptados });
    } catch (err) {
      console.error('Error al cargar proyectos:', err);
      setError(err.message || 'Error al cargar proyectos');
    } finally {
      setLoading(false);
    }
  };

  const openProjectDetail = async (id) => {
    try {
      const d = await fetchAuth(`/api/colaborador/proyectos/${id}/detalle/`);
      setProjectDetail(d);
      setProjectDetailOpen(true);
    } catch (error) {
      console.error('Error al cargar detalle:', error);
    }
  };

  const calcularProgreso = (completadas, totales) => {
    if (!totales) return 0;
    return Math.round((completadas / totales) * 100);
  };

  const formatDate = (s) => {
    const a = String(s || '')
    const part = a.split('T')[0].split(' ')[0]
    if (!part) return '-'
    const [y, m, d] = part.split('-')
    return `${d}/${m}/${y}`
  }

  const renderColab = (c) => {
    if (!c) return 'Sin asignar';
    if (typeof c === 'string') return c;
    if (typeof c === 'object') return c.nombre || c.email || 'Colaborador';
    return 'Sin asignar';
  };

  const parseTareaDescripcion = (desc) => {
    const s = String(desc || '');
    const etapaMatch = s.match(/\[ETAPA:(\d+)\]/);
    const etapaId = etapaMatch ? parseInt(etapaMatch[1], 10) : null;
    const etapaNames = { 1: 'Planificación', 2: 'Diseño', 3: 'Desarrollo', 4: 'Pruebas', 5: 'Implementación', 6: 'Cierre' };
    const fechaLimiteMatch = s.match(/\[FECHA_LIMITE_INICIAL:([0-9]{4}-[0-9]{2}-[0-9]{2})\]/);
    const fechaLimite = fechaLimiteMatch ? fechaLimiteMatch[1] : null;
    const fechas = Array.from(s.matchAll(/\[ENTREGA_FECHA:([^\]]+)\]/g)).map(m => m[1]);
    const comentarios = Array.from(s.matchAll(/\[ENTREGA_COMENTARIOS:([^\]]+)\]/g)).map(m => m[1]);
    const evidencias = Array.from(s.matchAll(/\[ENTREGA_EVIDENCIA_URL:([^\]]+)\]/g)).map(m => m[1]);
    const texto = s
      .replace(/\[ETAPA:\d+\]/g, '')
      .replace(/\[ETAPA_ACTUAL:\d+\]/g, '')
      .replace(/\[ETAPA_CERRADA:[^\]]+\]/g, '')
      .replace(/\[INVITACION_USUARIO:[^\]]+\]/g, '')
      .replace(/\[ENTREGA_FECHA:[^\]]+\]/g, '')
      .replace(/\[ENTREGA_COMENTARIOS:[^\]]+\]/g, '')
      .replace(/\[ENTREGA_EVIDENCIA_URL:[^\]]+\]/g, '')
      .replace(/\[FECHA_LIMITE_INICIAL:[^\]]+\]/g, '')
      .replace(/\s+/g, ' ')
      .trim();
    const entregas = [];
    const n = Math.max(fechas.length, comentarios.length, evidencias.length);
    for (let i = 0; i < n; i++) {
      entregas.push({ fecha: fechas[i] || null, comentario: comentarios[i] || null, evidencia: evidencias[i] || null });
    }
    return { etapaId, etapaNombre: etapaId ? (etapaNames[etapaId] || `Etapa ${etapaId}`) : null, fechaLimite, texto, entregas };
  };

  const toggleExpandModal = () => setModalExpanded(prev => !prev);
  const cerrarModal = () => { setProjectDetailOpen(false); setModalExpanded(false); };

  const handleAcceptProject = async (projectId) => {
    try {
      await fetchAuth('/api/colaborador/invitaciones/aceptar/', {
        method: 'POST',
        body: JSON.stringify({ id_proyecto: projectId })
      });
      await fetchProjects();
    } catch (e) {
      console.error('Error aceptando proyecto:', e);
    }
  };

  const handleRejectProject = async (projectId) => {
    try {
      await fetchAuth('/api/colaborador/invitaciones/rechazar/', {
        method: 'POST',
        body: JSON.stringify({ id_proyecto: projectId })
      });
      await fetchProjects();
    } catch (e) {
      console.error('Error rechazando proyecto:', e);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center">
        <div className="text-center">
          <div className="inline-block animate-spin rounded-full h-16 w-16 border-b-4 border-blue-600 mb-4"></div>
          <p className="text-slate-600 text-lg font-medium">Cargando proyectos...</p>
        </div>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-slate-50 to-slate-100 flex items-center justify-center p-6">
        <div className="bg-red-50 border border-red-200 rounded-2xl p-8 max-w-md">
          <div className="text-red-600 text-5xl mb-4">⚠️</div>
          <h3 className="text-red-800 text-xl font-bold mb-2">Error al cargar</h3>
          <p className="text-red-600 mb-4">{error}</p>
          <button 
            onClick={fetchProjects}
            className="bg-red-600 hover:bg-red-700 text-white font-semibold px-4 py-2 rounded-lg transition-colors"
          >
            Reintentar
          </button>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-slate-50 via-blue-50 to-slate-100 p-6">
      {/* Header */}
      <div className="max-w-7xl mx-auto mb-8">
        <div className="flex items-center gap-4 mb-6">
          <div className="bg-gradient-to-br from-blue-500 to-blue-600 p-4 rounded-2xl shadow-lg">
            <FaProjectDiagram className="text-white text-3xl" />
          </div>
          <div>
            <h1 className="text-4xl font-bold text-slate-800 tracking-tight">
              {isProyectosView ? 'Mis Proyectos' : 'Mis Invitaciones'}
            </h1>
            <p className="text-slate-600 mt-1">
              {isProyectosView ? 'Proyectos en los que colaboras' : 'Revisa y acepta solicitudes de proyectos'}
            </p>
          </div>
        </div>

        {/* Stats */}
        <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
          <div className="bg-white rounded-2xl shadow-lg p-5 border border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-amber-500 to-amber-600 p-3 rounded-xl">
                  <FaHourglassHalf className="text-white text-xl" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-800">{projects.solicitudes.length}</h3>
                  <p className="text-slate-600 text-sm">Solicitudes Pendientes</p>
                </div>
              </div>
            </div>
          </div>

          <div className="bg-white rounded-2xl shadow-lg p-5 border border-slate-200">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-3">
                <div className="bg-gradient-to-br from-emerald-500 to-emerald-600 p-3 rounded-xl">
                  <FaCheckCircle className="text-white text-xl" />
                </div>
                <div>
                  <h3 className="text-2xl font-bold text-slate-800">{projects.aceptados.length}</h3>
                  <p className="text-slate-600 text-sm">Proyectos Activos</p>
                </div>
              </div>
            </div>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="max-w-7xl mx-auto">
        {/* Solicitudes de Proyecto */}
        {isSolicitudesView && (
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200 mb-6">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <FaHourglassHalf className="text-amber-600" />
                Solicitudes de Proyecto
              </h3>
              {projects.solicitudes.length > 0 && (
                <span className="bg-amber-600 text-white text-sm font-bold px-3 py-1 rounded-full">
                  {projects.solicitudes.length}
                </span>
              )}
            </div>

            {projects.solicitudes.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FaCheckCircle className="text-5xl mx-auto mb-3 opacity-30" />
                <p className="font-medium">No tienes solicitudes pendientes</p>
                <p className="text-sm mt-1">Cuando te inviten a proyectos, aparecerán aquí</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 gap-4">
                {projects.solicitudes.map(p => (
                  <div
                    key={p.id_proyecto}
                    className="border border-slate-200 rounded-xl p-5 hover:border-amber-300 hover:shadow-md transition-all"
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-bold text-slate-800 text-lg mb-1">{p.nombre_proyecto}</h4>
                        <p className="text-sm text-slate-600">{p.descripcion || 'Sin descripción'}</p>
                        {p.etapa && (
                          <p className="text-xs text-slate-500 mt-1">Etapa Actual: {p.etapa?.nombre}</p>
                        )}
                      </div>
                      <span className="bg-amber-100 text-amber-700 text-xs font-semibold px-2 py-1 rounded-full whitespace-nowrap ml-2">
                        Pendiente
                      </span>
                    </div>

                    {p.fecha_inicio && (
                      <div className="flex items-center gap-1 text-xs text-slate-500 mb-4">
                        <FaCalendar />
                              <span>Inicio: {formatDate(p.fecha_inicio)}</span>
                      </div>
                    )}

                    <div className="flex gap-2 flex-wrap">
                      <button
                        onClick={() => openProjectDetail(p.id_proyecto)}
                        className="flex-1 bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-all flex items-center justify-center gap-2"
                      >
                        <FaInfoCircle />
                        Ver Detalle
                      </button>
                      <button
                        onClick={() => handleAcceptProject(p.id_proyecto)}
                        className="bg-gradient-to-r from-emerald-500 to-emerald-600 hover:from-emerald-600 hover:to-emerald-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-all flex items-center gap-2"
                      >
                        <FaCheck />
                        Aceptar
                      </button>
                      <button
                        onClick={() => handleRejectProject(p.id_proyecto)}
                        className="bg-gradient-to-r from-red-500 to-red-600 hover:from-red-600 hover:to-red-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-all flex items-center gap-2"
                      >
                        <FaTimes />
                        Rechazar
                      </button>
                    </div>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {/* Proyectos Aceptados */}
        {isProyectosView && (
          <div className="bg-white rounded-2xl shadow-lg p-6 border border-slate-200">
            <div className="flex items-center justify-between mb-6">
              <h3 className="text-xl font-bold text-slate-800 flex items-center gap-2">
                <FaCheckCircle className="text-emerald-600" />
                Proyectos Aceptados
              </h3>
              {projects.aceptados.length > 0 && (
                <span className="bg-emerald-600 text-white text-sm font-bold px-3 py-1 rounded-full">
                  {projects.aceptados.length}
                </span>
              )}
            </div>

            {projects.aceptados.length === 0 ? (
              <div className="text-center py-12 text-slate-500">
                <FaProjectDiagram className="text-5xl mx-auto mb-3 opacity-30" />
                <p className="font-medium">No tienes proyectos aceptados</p>
                <p className="text-sm mt-1">Acepta invitaciones para comenzar a colaborar</p>
              </div>
            ) : (
              <div className="grid grid-cols-1 lg:grid-cols-2 xl:grid-cols-3 gap-4">
                {projects.aceptados.map(p => (
                  <div
                    key={p.id_proyecto}
                    className="border border-slate-200 rounded-xl p-5 hover:border-emerald-300 hover:shadow-md transition-all group cursor-pointer"
                    onClick={() => openProjectDetail(p.id_proyecto)}
                  >
                    <div className="flex items-start justify-between mb-3">
                      <div className="flex-1">
                        <h4 className="font-bold text-slate-800 text-lg mb-1 group-hover:text-emerald-600 transition-colors">
                          {p.nombre_proyecto}
                        </h4>
                        <p className="text-sm text-slate-600 line-clamp-2">{p.descripcion || 'Sin descripción'}</p>
                        {p.etapa && (
                          <p className="text-xs text-slate-500 mt-1">Etapa Actual: {p.etapa?.nombre}</p>
                        )}
                      </div>
                    </div>

                    <div className="flex items-center justify-between mb-3">
                      <span className="bg-emerald-100 text-emerald-700 text-xs font-semibold px-2 py-1 rounded-full flex items-center gap-1">
                        <FaCheckCircle className="text-xs" />
                        Activo
                      </span>
                      {p.fecha_inicio && (
                        <div className="flex items-center gap-1 text-xs text-slate-500">
                          <FaCalendar />
                          <span>{formatDate(p.fecha_inicio)}</span>
                        </div>
                      )}
                    </div>

                    <button
                      onClick={(e) => {
                        e.stopPropagation();
                        openProjectDetail(p.id_proyecto);
                      }}
                      className="w-full bg-gradient-to-r from-blue-500 to-blue-600 hover:from-blue-600 hover:to-blue-700 text-white text-sm font-semibold px-4 py-2 rounded-lg transition-all flex items-center justify-center gap-2"
                    >
                      <FaInfoCircle />
                      Ver Detalle
                    </button>
                  </div>
                ))}
              </div>
            )}
          </div>
        )}
      </div>

      {/* Modal Detalle (vista jefe, solo lectura) */}
      {projectDetailOpen && projectDetail && (
        <div className="fixed inset-0 bg-black/50 backdrop-blur-sm flex items-center justify-center z-50 p-4" onClick={cerrarModal}>
          <div className="bg-white rounded-3xl shadow-2xl max-w-4xl w-full max-h-[90vh] overflow-hidden" onClick={(e) => e.stopPropagation()}>
            <div className="bg-gradient-to-r from-indigo-500 to-indigo-600 p-6 relative overflow-hidden">
              <div className="absolute top-0 right-0 w-96 h-96 bg-white/10 rounded-full -mr-48 -mt-48"></div>
              <div className="relative flex items-start justify-between">
                <div className="flex-1">
                  <div className="flex items-center gap-3 mb-2">
                    <FaProjectDiagram className="text-white text-2xl" />
                    <h2 className="text-2xl font-bold text-white">{projectDetail?.nombre_proyecto}</h2>
                  </div>
                  <p className="text-white/80 text-sm">{projectDetail?.descripcion || 'Sin descripción'}</p>
                </div>
                <div className="flex items-center gap-2 ml-4">
                  <button onClick={toggleExpandModal} className="bg-white/20 hover:bg-white/30 p-2 rounded-xl transition-all transform hover:scale-110" title={modalExpanded ? 'Ver menos' : 'Ver más detalles'}>
                    <FaInfoCircle className={`text-white text-xl transition-transform duration-300 ${modalExpanded ? 'rotate-180' : ''}`} />
                  </button>
                  <button onClick={cerrarModal} className="bg-white/20 hover:bg-white/30 p-2 rounded-xl transition-colors">
                    <FaTimes className="text-white text-xl" />
                  </button>
                </div>
              </div>
            </div>

            <div className={`overflow-y-auto transition-all duration-500 ease-in-out ${modalExpanded ? 'max-h-[70vh]' : 'max-h-[50vh]'}`}>
              <div className="space-y-6 p-6">
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="bg-gradient-to-br from-blue-50 to-blue-100 rounded-2xl p-5 border border-blue-200">
                    <div className="flex items-center gap-3 mb-2">
                      <FaCalendar className="text-blue-600 text-xl" />
                      <h3 className="font-bold text-blue-900">Fecha de Inicio</h3>
                    </div>
                    <p className="text-blue-700 text-lg font-semibold">{projectDetail.fecha_inicio ? formatDate(projectDetail.fecha_inicio) : 'Sin fecha'}</p>
                  </div>
                  <div className="bg-gradient-to-br from-purple-50 to-purple-100 rounded-2xl p-5 border border-purple-200">
                    <div className="flex items-center gap-3 mb-2">
                      <FaTasks className="text-purple-600 text-xl" />
                      <h3 className="font-bold text-purple-900">Estado de Tareas</h3>
                    </div>
                    <p className="text-purple-700 text-lg font-semibold">{(projectDetail.metricas?.tareas_completadas || 0)} / {(projectDetail.metricas?.tareas_totales || 0)} completadas</p>
                    <div className="w-full bg-purple-200 rounded-full h-2 mt-2">
                      <div className="bg-gradient-to-r from-purple-500 to-purple-600 h-2 rounded-full transition-all duration-500" style={{ width: `${calcularProgreso(projectDetail.metricas?.tareas_completadas || 0, projectDetail.metricas?.tareas_totales || 0)}%` }}></div>
                    </div>
                  </div>
                </div>

                {(Array.isArray(projectDetail.colaboradores) && projectDetail.colaboradores.length > 0) || (Array.isArray(projectDetail.tareas_proyecto) && projectDetail.tareas_proyecto.length > 0) ? (
                  <button onClick={toggleExpandModal} className="w-full bg-gradient-to-r from-indigo-50 to-indigo-100 hover:from-indigo-100 hover:to-indigo-200 text-indigo-700 font-semibold py-3 px-4 rounded-xl transition-all duration-300 flex items-center justify-center gap-2 border border-indigo-200">
                    <FaInfoCircle className={`transition-transform duration-300 ${modalExpanded ? 'rotate-180' : ''}`} />
                    <span>{modalExpanded ? 'Ver menos detalles' : 'Ver más detalles'}</span>
                    <FaChevronRight className={`transition-transform duration-300 ${modalExpanded ? 'rotate-90' : ''}`} />
                  </button>
                ) : null}

                <div className={`space-y-6 transition-all duration-500 ease-in-out overflow-hidden ${modalExpanded ? 'max-h-[2000px] opacity-100 mt-6' : 'max-h-0 opacity-0 mt-0'}`}>
                  {Array.isArray(projectDetail.colaboradores) && projectDetail.colaboradores.length > 0 && (
                    <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                      <div className="flex items-center gap-3 mb-4">
                        <FaUsers className="text-slate-600 text-2xl" />
                        <h3 className="text-xl font-bold text-slate-800">Colaboradores</h3>
                        <span className="bg-indigo-100 text-indigo-700 px-3 py-1 rounded-full text-sm font-semibold">{projectDetail.colaboradores.length}</span>
                      </div>
                      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
                        {projectDetail.colaboradores.map((colab, index) => (
                          <div key={index} className="bg-white rounded-xl p-4 flex items-center gap-3 border border-slate-200">
                            <div className="bg-indigo-100 p-3 rounded-full">
                              <FaUser className="text-indigo-600" />
                            </div>
                            <div>
                              <p className="font-semibold text-slate-800">{colab.nombre || 'Colaborador'}</p>
                              <p className="text-sm text-slate-500">{colab.email || 'Sin email'}</p>
                            </div>
                          </div>
                        ))}
                      </div>
                    </div>
                  )}

                  {Array.isArray(projectDetail.tareas_proyecto) && projectDetail.tareas_proyecto.length > 0 && (
                    <div className="bg-slate-50 rounded-2xl p-6 border border-slate-200">
                      <div className="flex items-center gap-3 mb-4">
                        <FaTasks className="text-slate-600 text-2xl" />
                        <h3 className="text-xl font-bold text-slate-800">Tareas del Proyecto</h3>
                      </div>
                      <div className="space-y-3 max-h-64 overflow-y-auto">
                        {projectDetail.tareas_proyecto.map((tarea, index) => (
                          <div key={index} className="bg-white rounded-xl p-4 border border-slate-200">
                            {(() => {
                              const meta = parseTareaDescripcion(tarea.descripcion);
                              const lastEntrega = meta.entregas.length ? meta.entregas[meta.entregas.length - 1] : null;
                              return (
                                <>
                                  <div className="flex items-start justify-between mb-2">
                                    <h4 className="font-semibold text-slate-800">{tarea.nombre || 'Tarea'}</h4>
                                    <div className="flex items-center gap-2">
                                      {meta.etapaNombre && (
                                        <span className="px-2 py-1 rounded-full text-[11px] font-semibold bg-indigo-100 text-indigo-700 border">Etapa: {meta.etapaNombre}</span>
                                      )}
                                      <span className="px-3 py-1 rounded-full text-xs font-semibold bg-slate-100 text-slate-700">{tarea.estado || 'Pendiente'}</span>
                                    </div>
                                  </div>
                                  <p className="text-sm text-slate-600">{meta.texto || 'Sin descripción'}</p>
                                  <div className="mt-2 grid grid-cols-1 sm:grid-cols-2 gap-2 text-xs text-slate-600">
                                    <div className="flex items-center gap-1">
                                      <FaCalendar className="text-slate-400" />
                                      <span>Fecha límite: {meta.fechaLimite ? formatDate(meta.fechaLimite) : '-'}</span>
                                    </div>
                                    {meta.entregas.length > 0 && (
                                      <div className="flex items-center gap-1">
                                        <FaTasks className="text-slate-400" />
                                        <span>Entregas: {meta.entregas.length}</span>
                                      </div>
                                    )}
                                  </div>
                                  {lastEntrega && (
                                    <div className="mt-2 p-3 bg-slate-50 rounded-lg border border-slate-200">
                                      <div className="flex items-center gap-2 text-xs text-slate-600">
                                        <FaCalendar className="text-slate-400" />
                                        <span>Última entrega: {formatDate(lastEntrega.fecha)}</span>
                                      </div>
                                      {lastEntrega.comentario && (
                                        <div className="flex items-center gap-2 text-xs text-slate-600 mt-1">
                                          <FaComment className="text-slate-400" />
                                          <span>{lastEntrega.comentario}</span>
                                        </div>
                                      )}
                                      {lastEntrega.evidencia && (
                                        <a href={lastEntrega.evidencia} target="_blank" rel="noreferrer" className="inline-flex items-center gap-1 text-xs font-semibold text-indigo-600 hover:text-indigo-700 mt-1">
                                          <FaLink />
                                          Ver evidencia
                                        </a>
                                      )}
                                    </div>
                                  )}
                                  {tarea.colaborador && (
                                    <div className="flex items-center gap-2 mt-2 text-sm text-slate-500">
                                      <FaUsers className="text-slate-400" />
                                      <span>Asignado a: {renderColab(tarea.colaborador)}</span>
                                    </div>
                                  )}
                                </>
                              );
                            })()}
                          </div>
                          ))}
                      </div>
                    </div>
                  )}
                </div>
              </div>
            </div>

            <div className="bg-slate-50 p-6 border-t border-slate-200 flex justify-end">
              <button onClick={cerrarModal} className="px-6 py-3 bg-slate-200 hover:bg-slate-300 text-slate-700 font-semibold rounded-xl transition-colors">Cerrar</button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
